import React, { useEffect, useState, useCallback } from "react";
import { Download, FileDown } from "lucide-react";
import api, { formatRp, formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useBranding } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function toCsv(rows, headers) {
  const csvHeaders = headers.map((h) => h.label).join(",");
  const lines = rows.map((r) =>
    headers.map((h) => {
      const v = r[h.key];
      if (v == null) return "";
      const str = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(str) ? `"${str}"` : str;
    }).join(",")
  );
  return [csvHeaders, ...lines].join("\n");
}

function download(filename, content, type = "text/csv;charset=utf-8") {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminReports() {
  const { branding } = useBranding();
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [start, setStart] = useState(monthStart.toISOString().slice(0, 10));
  const [end, setEnd] = useState(today.toISOString().slice(0, 10));
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const startIso = new Date(start).toISOString();
      const endIso = new Date(end + "T23:59:59").toISOString();
      const { data } = await api.get("/admin/reports/sales", { params: { start: startIso, end: endIso } });
      setReport(data);
    } catch (e) { notifyError(e); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    if (!report?.orders) return;
    const headers = [
      { key: "order_number", label: "No Pesanan" },
      { key: "created_at", label: "Tanggal" },
      { key: "user_name", label: "Pelanggan" },
      { key: "user_email", label: "Email" },
      { key: "status", label: "Status" },
      { key: "subtotal", label: "Subtotal" },
      { key: "shipping_cost", label: "Ongkir" },
      { key: "discount", label: "Diskon" },
      { key: "total", label: "Total" },
    ];
    download(`laporan-${start}-${end}.csv`, toCsv(report.orders, headers));
  };

  const exportPdf = () => {
    if (!report) return;
    const origin = window.location.origin;
    // SECURITY: HTML-escape all dynamic values to prevent stored XSS
    // (mis. user_name berisi <script>). Tanpa escape, document.write()
    // pada window baru bisa eksekusi script arbitrer.
    const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    }[c]));
    const safeStart = esc(start);
    const safeEnd = esc(end);
    const brandName = esc(branding.general.name);
    const brandTagline = esc(branding.general.tagline);
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Laporan Penjualan ${brandName}</title>
      <style>
        body{font-family:'Helvetica Neue',Arial,sans-serif;padding:32px;color:#4A2E1F}
        .head{display:flex;align-items:center;gap:14px;border-bottom:3px solid #F9C846;padding-bottom:16px;margin-bottom:20px}
        .head img{width:64px;height:64px}
        .brand{font-weight:800;font-size:24px;letter-spacing:-0.02em}
        .tagline{font-size:11px;text-transform:uppercase;letter-spacing:3px;color:#5A8F3D;font-weight:700}
        h1{color:#4A2E1F;font-size:20px;margin:0 0 4px}
        .period{color:#7A5440;font-size:14px}
        .summary{display:flex;gap:12px;margin:20px 0}
        .summary > div{flex:1;background:#FFF6DF;border:1px solid #F9C846;border-radius:12px;padding:14px}
        .summary .lbl{font-size:11px;text-transform:uppercase;color:#7A5440;font-weight:700;letter-spacing:1px}
        .summary .val{font-size:22px;font-weight:800;margin-top:4px;color:#4A2E1F}
        table{width:100%;border-collapse:collapse;font-size:12px;margin-top:8px}
        th,td{border:1px solid #E5E5E5;padding:8px 10px;text-align:left}
        th{background:#F9C846;color:#4A2E1F;font-weight:700}
        tbody tr:nth-child(even){background:#FFFDF5}
        .footer{margin-top:30px;padding-top:14px;border-top:1px solid #E5E5E5;font-size:11px;color:#7A5440;text-align:center}
      </style>
      </head><body>
      <div class="head">
        <img src="${esc(origin)}/brand/mark.svg" alt="logo" />
        <div>
          <div class="brand">${brandName}</div>
          <div class="tagline">${brandTagline}</div>
        </div>
      </div>
      <h1>Laporan Penjualan</h1>
      <div class="period">Periode: <b>${safeStart}</b> s.d. <b>${safeEnd}</b></div>
      <div class="summary">
        <div><div class="lbl">Total Pesanan</div><div class="val">${esc(report.total_orders)}</div></div>
        <div><div class="lbl">Total Omzet</div><div class="val">${esc(formatRp(report.total_revenue))}</div></div>
      </div>
      <table><thead><tr><th>No Pesanan</th><th>Tanggal</th><th>Pelanggan</th><th>Status</th><th>Total</th></tr></thead><tbody>
      ${report.orders.map((o) => `<tr><td>${esc(o.order_number)}</td><td>${esc(new Date(o.created_at).toLocaleDateString("id-ID"))}</td><td>${esc(o.user_name || "")}</td><td>${esc(o.status)}</td><td>${esc(formatRp(o.total))}</td></tr>`).join("")}
      </tbody></table>
      <div class="footer">Dicetak dari ${brandName} · ${esc(new Date().toLocaleString("id-ID"))}</div>
      </body></html>`;

    // SECURITY: ganti document.write() dengan Blob URL → safer (no script execution
    // via untrusted HTML pre-render dari window opener). Dengan blob URL, browser
    // menganggap dokumen sebagai resource baru, tidak inheriting privileges.
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (!w) {
      toast.error("Popup diblokir. Mohon izinkan popup untuk fitur cetak.");
      URL.revokeObjectURL(url);
      return;
    }
    // Trigger print setelah dokumen selesai load
    w.addEventListener("load", () => {
      try { w.print(); } catch (_) { /* user can press Ctrl+P manually */ }
    });
    // Cleanup blob URL setelah 1 menit (cukup untuk print dialog)
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <AdminLayout>
      <h1 className="font-display text-3xl font-black mb-4">Laporan Penjualan</h1>

      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 mb-4 flex flex-wrap gap-3 items-end">
        <div><Label>Dari</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl" /></div>
        <div><Label>Sampai</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl" /></div>
        <Button onClick={load} className="brand-btn-primary rounded-xl">Terapkan</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={!report} className="rounded-xl"><FileDown size={14} /> Excel/CSV</Button>
          <Button variant="outline" onClick={exportPdf} disabled={!report} className="rounded-xl"><Download size={14} /> PDF</Button>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-[#5C5C5C] font-bold">Total Pesanan</div>
              <div className="font-display text-3xl font-black mt-1">{report.total_orders}</div>
            </div>
            <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
              <div className="text-xs uppercase tracking-widest text-[#5C5C5C] font-bold">Total Omzet</div>
              <div className="font-display text-3xl font-black mt-1 text-[#E85D04]">{formatRp(report.total_revenue)}</div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold border-b border-[#E5E5E5]">
                  <th className="px-4 py-3">No</th>
                  <th className="px-4 py-3">Tanggal</th>
                  <th className="px-4 py-3">Pelanggan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {report.orders.map((o) => (
                  <tr key={o.id} className="border-b border-[#E5E5E5] last:border-0">
                    <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                    <td className="px-4 py-3 text-xs">{new Date(o.created_at).toLocaleDateString("id-ID")}</td>
                    <td className="px-4 py-3">{o.user_name}</td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase font-bold px-2 py-1 rounded bg-[#FAF9F6]">{o.status}</span></td>
                    <td className="px-4 py-3 font-bold">{formatRp(o.total)}</td>
                  </tr>
                ))}
                {report.orders.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[#5C5C5C]">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
